import { useEffect, useMemo, useState } from "react";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { DownloadSimple, Bank } from "@phosphor-icons/react";

const STORAGE_KEY = "necos.supplierExport.fields";
const PRESET_LABELS = { contact_sheet: "Contact sheet", full_profile: "Full profile", compliance: "Compliance", finance: "Finance" };

function FieldGroup({ group, selected, onToggle, onToggleGroup }) {
  const keys = group.fields.map(f => f.key);
  const picked = keys.filter(k => selected.has(k)).length;
  const all = picked === keys.length;
  return (
    <div className="border border-border rounded-md p-3" data-testid={`export-group-${group.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1">
          {group.id === "finance" && <Bank size={13} className="text-amber-600" />}{group.label}
          <span className="text-muted-foreground font-normal normal-case tracking-normal">· {picked}/{keys.length}</span>
        </p>
        <button type="button" className="text-[11px] text-primary hover:underline" onClick={() => onToggleGroup(keys, !all)} data-testid={`export-group-toggle-${group.id}`}>
          {all ? "Clear" : "Select all"}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5">
        {group.fields.map(f => (
          <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={selected.has(f.key)} onCheckedChange={() => onToggle(f.key)} data-testid={`export-field-${f.key}`} />
            <span className={selected.has(f.key) ? "" : "text-muted-foreground"}>{f.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function SupplierExportDialog({ open, onOpenChange, visibleVendors = [], totalCount = 0 }) {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [scope, setScope] = useState("view");
  const [format, setFormat] = useState("csv");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    API.get("/suppliers/export/fields").then(({ data }) => {
      setCatalog(data);
      const allowed = new Set(data.groups.flatMap(g => g.fields.map(f => f.key)));
      let saved = [];
      try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch {}
      const initial = saved.filter(k => allowed.has(k));
      setSelected(new Set(initial.length ? initial : data.presets.contact_sheet));
    }).catch(() => toast.error("Could not load export fields"));
  }, [open]);

  const groups = catalog?.groups || [];
  const orderedKeys = useMemo(() => groups.flatMap(g => g.fields.map(f => f.key)).filter(k => selected.has(k)), [groups, selected]);
  const includesBank = orderedKeys.some(k => k.startsWith("bank_"));
  const count = scope === "view" ? visibleVendors.length : totalCount;

  const toggle = (k) => setSelected(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleGroup = (keys, on) => setSelected(prev => { const n = new Set(prev); keys.forEach(k => on ? n.add(k) : n.delete(k)); return n; });
  const applyPreset = (id) => setSelected(new Set(catalog?.presets?.[id] || []));

  const runExport = async () => {
    if (!orderedKeys.length) { toast.error("Pick at least one field"); return; }
    setBusy(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(orderedKeys));
      const body = { fields: orderedKeys, format };
      if (scope === "view") body.supplier_ids = visibleVendors.map(v => v.id);
      const res = await API.post("/suppliers/export", body, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const name = /filename="?([^"]+)"?/.exec(disposition)?.[1] || `suppliers-export.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`Exported ${res.headers["x-export-count"] ?? count} supplier(s) · ${orderedKeys.length} field(s)`);
      onOpenChange(false);
    } catch (e) {
      let msg = "Export failed";
      try { msg = JSON.parse(await e?.response?.data?.text())?.detail || msg; } catch {}
      toast.error(typeof msg === "string" ? msg : "Export failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="supplier-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Export Suppliers</DialogTitle>
          <DialogDescription>Choose exactly what to take with you — the file only contains the fields you tick.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="border border-border rounded-md p-3 space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider">Which suppliers</p>
              {[["view", `Current view (${visibleVendors.length})`], ["all", `All suppliers (${totalCount})`]].map(([v, label]) => (
                <label key={v} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="export-scope" value={v} checked={scope === v} onChange={() => setScope(v)} data-testid={`export-scope-${v}`} />
                  {label}
                </label>
              ))}
            </div>
            <div className="border border-border rounded-md p-3 space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider">Format</p>
              {[["csv", "CSV (.csv) — opens anywhere"], ["xlsx", "Excel (.xlsx)"], ["json", "JSON — for developers / imports"]].map(([v, label]) => (
                <label key={v} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="export-format" value={v} checked={format === v} onChange={() => setFormat(v)} data-testid={`export-format-${v}`} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground mr-1">Quick picks:</span>
            {Object.keys(catalog?.presets || {}).map(id => (
              <Button key={id} type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => applyPreset(id)} data-testid={`export-preset-${id}`}>
                {PRESET_LABELS[id] || id}
              </Button>
            ))}
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelected(new Set())} data-testid="export-clear-all">Clear all</Button>
          </div>

          {!catalog ? <p className="text-sm text-muted-foreground">Loading fields…</p> : (
            <div className="space-y-2">
              {groups.map(g => <FieldGroup key={g.id} group={g} selected={selected} onToggle={toggle} onToggleGroup={toggleGroup} />)}
            </div>
          )}

          {includesBank && (
            <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2" data-testid="export-bank-warning">
              This export includes supplier bank details. Store the file securely and delete it once imported elsewhere — the export is recorded in the audit log.
            </p>
          )}

          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-muted-foreground" data-testid="export-summary">{count} supplier(s) · {orderedKeys.length} field(s) · {format.toUpperCase()}</p>
            <Button onClick={runExport} disabled={busy || !orderedKeys.length || !count} data-testid="export-run-btn">
              <DownloadSimple size={15} className="mr-1" /> {busy ? "Preparing…" : "Download export"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
